import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTimeline } from '../../hooks/useReviews'
import type { TimelineEvent, Finding } from '../../gen/types'
import { FindingCard } from '../FindingCard/FindingCard'

interface KindConfig {
  label: string
  icon: string
}

const KIND_CONFIG: Record<string, KindConfig> = {
  'revision':      { label: 'Revision',     icon: '⟳' },
  'signal':        { label: 'Signal',       icon: '◉' },
  'finding':       { label: 'Finding',      icon: '⚑' },
  'author-reply':  { label: 'Author Reply', icon: '◎' },
  'agent-patch':   { label: 'Agent Patch',  icon: '⚙' },
  'decision':      { label: 'Decision',     icon: '⊕' },
  'question':      { label: 'Question',     icon: '?' },
  'answer':        { label: 'Answer',       icon: '✓' },
  'human-comment': { label: 'Comment',      icon: '⬡' },
  'memory-cited':  { label: 'Memory Cited', icon: '◆' },
  'author_reply':  { label: 'Author Reply', icon: '◎' },
  'agent_patch':   { label: 'Agent Patch',  icon: '⚙' },
  'human_comment': { label: 'Comment',      icon: '⬡' },
  'memory_cited':  { label: 'Memory Cited', icon: '◆' },
}

const ATTENTION_KINDS = new Set([
  'finding', 'decision', 'human-comment', 'question', 'answer', 'human_comment',
])

function formatTime(iso: string): string {
  try {
    const d = new Date(iso)
    const today = new Date()
    const isToday =
      d.getFullYear() === today.getFullYear() &&
      d.getMonth() === today.getMonth() &&
      d.getDate() === today.getDate()
    if (isToday) {
      return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    }
    return d.toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return iso
  }
}

interface EventItemProps {
  event: TimelineEvent
  finding?: Finding
  comments?: TimelineEvent[]
  reviewId: string
  // The /<tenant>/<repo>/reviews/<name> URL prefix for this review. Empty
  // string is acceptable; the diff-link handler falls back to /reviews/<id>.
  reviewBasePath?: string
  expandKey: number
  collapseKey: number
}

function EventItem({ event, finding, comments = [], reviewId, reviewBasePath, expandKey, collapseKey }: EventItemProps) {
  const navigate = useNavigate()
  const cfg = KIND_CONFIG[event.kind] ?? { label: event.kind, icon: '•' }
  const isFinding = event.kind === 'finding'
  const isAttention = ATTENTION_KINDS.has(event.kind)
  const [expanded, setExpanded] = useState(isAttention)
  const [resolved, setResolved] = useState(finding?.status === 'resolved')

  const anchor = finding?.anchor ?? null
  const anchorText = anchor?.range
    ? `${anchor.path}:${anchor.range.startLine}${anchor.range.endLine !== anchor.range.startLine ? `–${anchor.range.endLine}` : ''}`
    : anchor?.path ?? null
  const handleAnchorClick = useCallback(() => {
    if (!anchor?.path) return
    const params = new URLSearchParams({ file: anchor.path })
    if (anchor.range?.startLine) params.set('line', String(anchor.range.startLine))
    // URL triplet (tenant/repo/reviewName) comes from the parent (ReviewDetailPage)
    // which has the resolved Review object. Falls back to /reviews/<id>/diff
    // only on the unlikely race where reviewBasePath isn't yet populated.
    const base = reviewBasePath || `/reviews/${reviewId}`
    navigate(`${base}/diff?${params.toString()}`)
  }, [anchor, reviewId, reviewBasePath, navigate])

  const prevExpand = useRef(expandKey)
  const prevCollapse = useRef(collapseKey)

  useEffect(() => {
    if (expandKey !== prevExpand.current) {
      prevExpand.current = expandKey
      setExpanded(true)
    }
  }, [expandKey])

  useEffect(() => {
    if (collapseKey !== prevCollapse.current) {
      prevCollapse.current = collapseKey
      setExpanded(false)
    }
  }, [collapseKey])

  const actor =
    event.actorKind && event.actorId
      ? `${event.actorKind} · ${event.actorId.slice(0, 14)}`
      : event.actorId || event.actorKind || null

  const handleResolve = useCallback(() => setResolved(true), [])
  const handleUnresolve = useCallback(() => setResolved(false), [])

  const isEffectivelyResolved = resolved || finding?.status === 'resolved'

  const stripeClass = isEffectivelyResolved
    ? 'border-l-status-open'
    : isAttention
      ? 'border-l-accent'
      : 'border-l-transparent'

  const bgClass = isEffectivelyResolved
    ? 'bg-status-open/[0.04]'
    : isAttention
      ? 'bg-accent-bg/[0.4]'
      : 'bg-canvas'

  const iconColor = isEffectivelyResolved
    ? 'text-status-open'
    : isAttention
      ? 'text-accent'
      : 'text-fg-dim'

  return (
    <div
      id={`evt-${event.id}`}
      className={[
        'border border-border-subtle border-l-[3px] rounded-[4px]',
        stripeClass,
        bgClass,
      ].join(' ')}
    >
      <button
        className="w-full flex items-center gap-2 px-3 py-2 cursor-pointer select-none bg-transparent border-0 text-left"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
      >
        <span className={`w-5 text-center text-[13px] ${iconColor}`} aria-hidden>
          {cfg.icon}
        </span>
        <span className="font-semibold text-[12px] text-fg min-w-[90px]">{cfg.label}</span>
        {actor && (
          <span className="text-[11px] text-fg-dim font-mono flex-1 truncate min-w-0">
            {actor}
          </span>
        )}
        {isEffectivelyResolved && (
          <span className="text-[10px] text-status-open font-mono uppercase tracking-[0.04em]">
            ✓ resolved
          </span>
        )}
        <time className="text-[11px] text-fg-dim font-mono" dateTime={event.at}>
          {formatTime(event.at)}
        </time>
        <span className="text-[9px] text-fg-dim ml-1">{expanded ? '▾' : '▸'}</span>
      </button>

      {expanded && (
        <div className="px-[14px] pb-3 pt-1 pl-9 border-t border-border-subtle">
          {isFinding && finding ? (
            <FindingCard
              finding={finding}
              reviewId={reviewId}
              comments={comments}
              isResolved={isEffectivelyResolved}
              onResolve={handleResolve}
              onUnresolve={handleUnresolve}
              anchorText={anchorText}
              onAnchorClick={handleAnchorClick}
            />
          ) : (
            <div className="flex flex-col gap-[6px]">
              {event.message && (
                <div className="text-[12px] text-fg-muted leading-[1.55] mt-2">
                  {event.message}
                </div>
              )}
              {event.actorKind && (
                <DetailRow label="actor">
                  {event.actorKind} · {event.actorId}
                </DetailRow>
              )}
              {event.revisionId && (
                <DetailRow label="rev">{event.revisionId.slice(0, 12)}</DetailRow>
              )}
              <DetailRow label="id">{event.id.slice(0, 16)}</DetailRow>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2 text-[11px] font-mono">
      <span className="text-fg-dim uppercase tracking-[0.04em] w-12">{label}</span>
      <span className="text-fg-muted">{children}</span>
    </div>
  )
}

interface AccordionTimelineProps {
  reviewId: string
  reviewBasePath?: string
  findings?: Finding[]
  expandKey?: number
  collapseKey?: number
}

export default function AccordionTimeline({
  reviewId,
  reviewBasePath,
  findings = [],
  expandKey = 0,
  collapseKey = 0,
}: AccordionTimelineProps) {
  const { data: events, isLoading, isError } = useTimeline(reviewId)

  const findingById = Object.fromEntries(findings.map((f) => [f.id, f]))

  const commentsByFinding: Record<string, TimelineEvent[]> = {}
  const topLevelEvents: TimelineEvent[] = []
  for (const evt of events ?? []) {
    if (evt.kind === 'human-comment' && evt.findingId) {
      ;(commentsByFinding[evt.findingId] ??= []).push(evt)
    } else {
      topLevelEvents.push(evt)
    }
  }

  if (isLoading) {
    return <div className="card-bd text-fg-dim text-[12px]">Loading timeline…</div>
  }
  if (isError) {
    return <div className="card-bd text-sev-blocking text-[12px]">Failed to load timeline</div>
  }
  if (!events || events.length === 0) {
    return <div className="card-bd text-fg-dim text-[12px]">No timeline events yet.</div>
  }

  return (
    <div className="flex flex-col gap-[6px] p-2">
      {topLevelEvents.map((evt) => (
        <EventItem
          key={evt.id}
          event={evt}
          finding={evt.findingId ? findingById[evt.findingId] : undefined}
          comments={evt.findingId ? commentsByFinding[evt.findingId] ?? [] : []}
          reviewId={reviewId}
          reviewBasePath={reviewBasePath}
          expandKey={expandKey}
          collapseKey={collapseKey}
        />
      ))}
    </div>
  )
}
